import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import CustomInput from "../TextField/TextFeild";
import TimezoneSelect from "../TimeZoneDropDown/TimezoneSelect";
import moment from "moment-timezone";
import { createPoll, startPoll } from "../../store/slices/pollSlice";
import { useSocket } from "./../../context/SocketContext";
import {
  FaPlus,
  FaImage,
  FaTimes
} from "react-icons/fa";

// Import CKEditor
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import CropperModalAdmin from "./CropperModalAdmin";
import { HUMAN_TIMEZONES } from "../../constants";

const CreatePoll = ({ show, onClose }) => {
  const { polls } = useSelector((state) => state.poll);
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [step, setStep] = useState(2);
  const [answers, setAnswers] = useState(["", ""]);
  const { socket } = useSocket();
  const [showModal, setShowModal] = useState(false);
  const [image, setImage] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [deletedAnswers, setDeletedAnswers] = useState([]);
  const [isImageDeleted, setIsImageDeleted] = useState(false);

  const roomId = auth?.user?.attributes?.stationChatId;

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .required("Title is required and cannot be longer than 200 characters")
      .max(200, "Title cannot be longer than 200 characters"),

    answers: Yup.array()
      .of(
        Yup.string()
          .required("Answer is required")
          .max(200, "Answer cannot be longer than 200 characters")
      )
      .min(2, "Must contain at least 2 filled answers"),

    timezone: Yup.string(),
    rules: Yup.string().max(200, "Title cannot be longer than 200 characters"),
    startDate: Yup.string().when("timezone", {
      is: (tz) => tz && tz.length > 0,
      then: (schema) => schema.required("Start date is required when timezone is selected"),
    }),

    startTime: Yup.string().when("timezone", {
      is: (tz) => tz && tz.length > 0,
      then: (schema) =>
        schema
          .required("Start time is required when timezone is selected")
          .test(
            "is-future-5-min",
            "Start time must be at least 5 minutes in the future",
            function (value) {
              const { startDate, timezone } = this.parent;
              const tz = HUMAN_TIMEZONES[timezone];

              if (!startDate || !value || !timezone) return true;

              // Convert to moment object
              const start = moment.tz(
                `${startDate} ${value}`,
                "YYYY-MM-DD HH:mm",
                tz
              );
              const now = moment.tz(tz);
              const minutesUntil = start.diff(now, 'minutes', true);

              console.log("timezone:", timezone);
              console.log("startInTZ:", start.format());
              console.log("now:", now.format());
              console.log("minutesUntil:", minutesUntil);

              // Validation
              if (minutesUntil < 5) {
                return this.createError({
                  message: `Start Time must be 5 minutes in advance.`
                });
              }

              return true;
            }
          ),
    }),

    endDate: Yup.string().when("timezone", {
      is: (tz) => tz && tz.length > 0,
      then: (schema) => schema.required("End date is required when timezone is selected"),
    }),

    endTime: Yup.string().when(["timezone", "startDate", "startTime"], {
      is: (tz, sd, st) => tz && sd && st,
      then: (schema) =>
        schema
          .required("End time is required when timezone is selected")
          .test(
            "is-after-start",
            "End time must be after the start time",
            function (value) {
              let { startDate, startTime, endDate, timezone } = this.parent;
              timezone = HUMAN_TIMEZONES[timezone];
              if (!startDate || !startTime || !endDate || !value || !timezone)
                return true;

              const start = moment.tz(
                `${startDate} ${startTime}`,
                "YYYY-MM-DD HH:mm",
                timezone
              );

              const end = moment.tz(
                `${endDate} ${value}`,
                "YYYY-MM-DD HH:mm",
                timezone
              );

              return end.isAfter(start);
            }
          ),
    }),
  });


  const handleCrop = (croppedImg) => {
    console.log(croppedImg, 'croppedImg')
    setCroppedImage(croppedImg);
    formik.setFieldValue("image", croppedImg);
    setShowModal(false);
  };
  const removeImage = () => {
    setCroppedImage(null);
    setImage(null);
    formik.setFieldValue("image", "");
    setIsImageDeleted(false);
  };


  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImage(reader.result);
        setShowModal(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const formik = useFormik({
    initialValues: {
      title: "",
      answers: ["", ""],
      timezone: "",
      startDate: "",
      startTime: "",
      endDate: "",
      endTime: "",
      image: "", // This will store the cropped image
      description: "", // This will store the CKEditor content
    },
    validationSchema,
    enableReinitialize: true,
    validateOnChange: false,
    context: { step },
    onSubmit: async (values, { resetForm }) => {
      if (step === 1) {
        const errors = await formik.validateForm();
        if (Object.keys(errors).length === 0) {
          setStep(2); // Go to scheduling step
        }
      } else {
        // Filter out deleted answers
        const activeAnswers = values.answers.filter((_, index) => !deletedAnswers.includes(index));
        
        // The description is already in values.description
        const pollData = {
          ...values,
          answers: activeAnswers,
          image: isImageDeleted ? "" : values.image,
          // No need to manually add description here as it's already in values
        };

        dispatch(createPoll({ data: pollData, socket }))
          .then((data) => {
            const pollId = data.payload.id;
            if (!values.timezone) {
              return dispatch(startPoll({ id: pollId, socket })).then(() => {
                socket.emit(`poll.start`, { poll: data.payload, roomId });
              });
            }
            return pollId;
          })
          .then((pollId) => {
            resetForm();
            setStep(1);
            setAnswers(["", ""]);
            setCroppedImage(null);
            onClose();
          });
      }
    },
  });

  useEffect(() => {
    if (show) {
      formik.resetForm();
      setAnswers(["", ""]);
      setCroppedImage(null);
      setDeletedAnswers([]);
      setIsImageDeleted(false);
      setStep(1);
    }
  }, [show]);

  const handleAnswerChange = (index, value) => {
    const newAnswers = [...formik.values.answers];
    newAnswers[index] = value;
    formik.setFieldValue("answers", newAnswers);
  };

  const addAnswer = () => {
    if (formik.values.answers.length < 4) {
      formik.setFieldValue("answers", [...formik.values.answers, ""]);
    }
  };

  const handleDeleteAnswer = (index) => {
    setDeletedAnswers([...deletedAnswers, index]);
  };

  const handleUndoDeleteAnswer = (index) => {
    setDeletedAnswers(deletedAnswers.filter((i) => i !== index));
  };

  const removeAnswer = (index) => {
    const newAnswers = formik.values.answers.filter((_, i) => i !== index);
    formik.setFieldValue("answers", newAnswers);
    // Remove from deletedAnswers if it was there
    setDeletedAnswers(deletedAnswers.filter((i) => i !== index).map(i => i > index ? i - 1 : i));
  };

  const handleDeleteImage = () => {
    setIsImageDeleted(true);
  };

  const handleUndoDeleteImage = () => {
    setIsImageDeleted(false);
  };

  return (
    <>
      <Modal
        show={show}
        size="xl"
        onHide={onClose}
        centered
        backdrop="static"
        keyboard={false}
      >
        <form onSubmit={formik.handleSubmit} className="row m-3">
          <div className="col-md-12 " style={{ borderBottom: "1px solid grey" }}>
            {step === 1 && (
              <>
                <h4 className="mb-2 d-flex">Create Poll</h4>
                <p className="text-muted mb-3">
                  You can create a poll and save it as a draft or immediately
                  start it. You will be able to alter it and manually start and
                  stop it.
                </p>
              </>
            )
            }
            {step === 2 && (
              <>
                <h4 className="mb-2">Start Poll Now or Schedule for Later</h4>
                <p className="text-muted mb-3">
                  You can schedule polls to begin at your preferred time and date
                  in the future.
                </p>
              </>
            )}

          </div>

          <div className="col-md-6 shadow-none p-3 m-0" style={{ borderRight: "1px solid grey" }}>


            {step === 1 && (
              <>
                <CustomInput
                  label="Question"
                  id="title"
                  name="title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.title && formik.errors.title}
                  required
                />

                {formik.values.answers.map((answer, index) => (
                  <div key={index} className="d-flex align-items-center mb-3" style={{ position: "relative" }}>
                    <div className="flex-grow-1" style={{ position: "relative", zIndex: deletedAnswers.includes(index) ? 1 : 2 }}>
                      <CustomInput
                        label={`Answer ${index + 1}`}
                        id={`answers[${index}]`}
                        name={`answers[${index}]`}
                        value={formik.values.answers[index]}
                        onChange={(e) =>
                          handleAnswerChange(index, e.target.value)
                        }
                        onBlur={formik.handleBlur}
                        error={
                          !deletedAnswers.includes(index) &&
                          formik.errors.answers &&
                            formik.touched.answers &&
                            typeof formik.errors.answers[index] === "string"
                            ? formik.errors.answers[index]
                            : null
                        }
                        required
                        disabled={deletedAnswers.includes(index)}
                      />
                    </div>
                    {formik.values.answers.length > 2 && !deletedAnswers.includes(index) && (
                      <button
                        type="button"
                        className="btn text-danger ms-2"
                        onClick={() => handleDeleteAnswer(index)}
                        title="Delete"
                        style={{ zIndex: 2 }}
                      >
                        Delete
                      </button>
                    )}
                    {deletedAnswers.includes(index) && (
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          background: "rgba(0, 0, 0, 0.65)",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          zIndex: 5,
                          borderRadius: "4px",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => handleUndoDeleteAnswer(index)}
                          className="simpleBtn"
                        >
                          <span>UNDO DELETE</span>
                        </button>
                      </div>
                    )}
                  </div>
                ))}

                {formik.values.answers.length < 4 && (
                  <div className="mb-4">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={addAnswer}
                    >
                      Add Answer
                    </button>
                  </div>
                )}


              </>
            )}

            {step === 2 && (
              <>
                <TimezoneSelect
                  label="Timezone"
                  id="timezone"
                  name="timezone"
                  value={formik.values.timezone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.timezone && formik.errors.timezone}
                />

                <div className="row">
                  <div className="col-md-6">
                    <CustomInput
                      type="date"
                      label="Start Date"
                      name="startDate"
                      value={formik.values.startDate}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.startDate && formik.errors.startDate}
                    />
                  </div>
                  <div className="col-md-6">
                    <CustomInput
                      type="time"
                      label="Start Time"
                      name="startTime"
                      value={formik.values.startTime}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.startTime && formik.errors.startTime}
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <CustomInput
                      type="date"
                      label="End Date"
                      name="endDate"
                      value={formik.values.endDate}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.endDate && formik.errors.endDate}
                    />
                  </div>
                  <div className="col-md-6">
                    <CustomInput
                      type="time"
                      label="End Time"
                      name="endTime"
                      value={formik.values.endTime}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.endTime && formik.errors.endTime}
                    />
                  </div>
                </div>


              </>
            )}
          </div>
          <div className="col-md-6 border-right p-3 " >
            <h6 className="mb-3 text-decoration-underline text-center">Optional Information </h6>
            <div className="col-md-12">
              <div className="d-flex flex-column align-items-center">
                <div className="position-relative mb-3 w-100" style={{ position: "relative" }}>
                  {croppedImage && !isImageDeleted ? (
                    <div className="d-flex align-items-center">
                      <img
                        src={croppedImage}
                        alt="Contest"
                        className="img-fluid"
                        style={{
                          width: "90%",
                          height: "120px",
                          objectFit: "contain",
                          border: "2px solid #ddd",
                          borderRadius: "8px",
                          backgroundColor: "#f8f9fa"
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          // If image fails to load, show upload area
                          setCroppedImage(null);
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleDeleteImage}
                        style={{ border: "0px", outline: "0px", cursor: "pointer", background: "none", marginLeft: "10px" }}
                      >
                        Delete
                      </button>
                    </div>
                  ) : croppedImage && isImageDeleted ? (
                    <div className="position-relative">
                      <img
                        src={croppedImage}
                        alt="Contest"
                        className="img-fluid"
                        style={{
                          width: "100%",
                          height: "120px",
                          objectFit: "contain",
                          border: "2px solid #ddd",
                          borderRadius: "8px",
                          backgroundColor: "#f8f9fa"
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          background: "rgba(0, 0, 0, 0.65)",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          zIndex: 5,
                          borderRadius: "8px",
                        }}
                      >
                        <button
                          type="button"
                          onClick={handleUndoDeleteImage}
                          className="simpleBtn"
                        >
                          <span>UNDO DELETE</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="d-flex flex-column align-items-center justify-content-center position-relative"
                      style={{
                        width: "100%",
                        height: "100px",
                        backgroundColor: "#f8f9fa",
                        border: "2px dashed #ddd",
                        borderRadius: "8px",
                        cursor: "pointer"
                      }}
                      onClick={() => document.getElementById('profileUpload').click()}
                    >
                      <FaImage size={40} color="#aaa" className="mb-2" />
                      <small className="text-muted">
                        Click to change/upload
                      </small>
                    </div>
                  )}
                  <label
                    htmlFor="profileUpload"
                    className="btn btn-sm btn-primary position-absolute"
                    style={{
                      bottom: "-15px",
                      right: "50%",
                      transform: "translateX(50%)",
                      borderRadius: "50%",
                      width: "30px",
                      height: "30px",
                      padding: "0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                    }}
                  >
                    <FaPlus size={12} />
                  </label>
                  <input
                    type="file"
                    id="profileUpload"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                </div>
              </div>

              {/* CKEditor Section */}
              <div className="my-4">
                <label htmlFor="poll-description" className="form-label">
                  Additional Information <small className="text-muted">(Optional)</small>
                </label>
                <div className="ckeditor-container" style={{ minHeight: "100px" }}>
                  <CKEditor
                    editor={ClassicEditor}
                    data={formik.values.description} // Use formik value
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      formik.setFieldValue("description", data); // Update formik value
                    }}
                    config={{
                      placeholder: "Add a detailed description for your poll...",
                      toolbar: {
                        items: [
                          'heading', '|',
                          'bold', 'italic', 'underline', 'strikethrough', '|',
                          'fontColor', 'fontBackgroundColor', '|',
                          'bulletedList', 'numberedList', '|',
                          'alignment', '|',
                          'link', '|',
                          'undo', 'redo'
                        ]
                      },
                      language: 'en'
                    }}
                    onReady={(editor) => {
                      editor.editing.view.change((writer) => {
                        writer.setStyle(
                          'height',
                          '100px',
                          editor.editing.view.document.getRoot()
                        );
                      });
                    }}
                  />
                </div>
                {formik.values.description && (
                  <div className="mt-2">
                    <small className="text-muted">
                      Character count: {formik.values.description.replace(/<[^>]*>/g, '').length}
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="col-md-12 ">
            {step === 1 && (
              <div className="d-flex justify-content-end mt-3 modalBtnContainer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save as Draft
                </button>
                <button type="submit" className="btn btn-success">
                  Create and Schedule
                </button>
              </div>
            )
            }
            {step === 2 && (
              <div className="d-flex justify-content-end mt-3 gap-2 ">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setStep(1)}
                >
                  Back
                </button>

                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={
                    !formik.values.startDate &&
                    !formik.values.startTime &&
                    !formik.values.endDate &&
                    !formik.values.endTime &&
                    !formik.values.timezone
                  }
                >
                  {formik.isSubmitting ? "Scheduling..." : "Schedule Poll"}
                </button>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    formik.values.startDate ||
                    formik.values.startTime ||
                    formik.values.endDate ||
                    formik.values.endTime ||
                    formik.values.timezone
                  }
                  onClick={() => {
                    // Clear the schedule fields before submitting
                    formik.setFieldValue("startDate", "");
                    formik.setFieldValue("startTime", "");
                    formik.setFieldValue("endDate", "");
                    formik.setFieldValue("endTime", "");
                    formik.setFieldValue("timezone", "");
                  }}
                >
                  Start Now
                </button>
              </div>
            )}

          </div>
        </form>
      </Modal>
      <CropperModalAdmin
        heading="Poll Image"
        show={showModal}
        onClose={() => setShowModal(false)}
        image={image}
        onCrop={handleCrop}
        aspectRatio={1242 / 180}
      />
    </>
  );
};

export default CreatePoll;